import React, { useEffect, useRef, useState } from 'react';
import { Device } from '@twilio/voice-sdk';
import Appearance from 'styles/Appearance.js';
import Button from 'views/Button.js';
import Request from 'files/Request.js';

// Global handler for unhandled promise rejections - catches user gesture errors from Twilio SDK
const handleUnhandledRejection = (event) => {
    if(event.reason?.name === 'NotAllowedError' || event.reason?.message?.includes('user gesture') || event.reason?.message?.includes('NotAllowedError')) {
        console.warn('User gesture error suppressed:', event.reason?.message || event.reason);
        event.preventDefault();
        event.stopImmediatePropagation();
        return false;
    }
};

const CallButton = ({ lead, style, title, utils }) => {

    const currentCallRef = useRef(null);
    const deviceRef = useRef(null);
    const durationInterval = useRef(null);
    const startTime = useRef(null);
    const tokenRef = useRef(null);

    const [callState, setCallState] = useState({
        duration: 0,
        error: null,
        muted: false,
        status: 'idle'
    });
    const [deviceReady, setDeviceReady] = useState(false);
    const [permissionGranted, setPermissionGranted] = useState(false);

    const onEndCallClick = () => {
        setCallState(prev => ({ ...prev, status: 'disconnecting' }));
        if(currentCallRef.current) {
            try {
                currentCallRef.current.disconnect();
            } catch(e) {
                console.warn('Error disconnecting call:', e);
            }
            currentCallRef.current = null;
        }
    }

    const onMuteToggleClick = () => {
        const muted = !callState.muted;
        if(currentCallRef.current) {
            currentCallRef.current.mute(muted);
            setCallState(prev => ({ ...prev, muted }));
        }
    }

    const onStartCallClick = async () => {
        try {

            // throw an error if no phone number was provided
            if(!lead || !lead.phone_number) {
                throw new Error('No phone number was provided for this call.');
            }

            // device must be initialized before calling
            if(!deviceRef.current || !deviceReady) {
                throw new Error('Device is not ready. Please refresh the page and try again.');
            }

            // update call state with connecting status
            setCallState(prev => ({ ...prev, status: 'connecting' }));

            // sanitize phone number to E.164 format
            const sanitizedPhone = lead.phone_number.replace(/[\s\-\(\)\.]/g, '');

            // prepare call parameters
            const params = { 
                client_id: utils.client.get().id,
                lead_id: lead.id,
                To: sanitizedPhone,
                user_id: utils.user.get().user_id
            };
            
            // connect synchronously to preserve user gesture
            const call = await deviceRef.current.connect({ params }).catch(reportError);
            
            // if connect was suppressed due to user gesture error, return early
            if(!call) {
                setCallState(prev => ({ ...prev, status: 'idle' }));
                return;
            }
            
            currentCallRef.current = call;

            // register call event listeners
            call.on('error', reportError);
            
            call.on('accept', () => {

                // with answerOnBridge=true, accept fires when customer answers
                setCallState(prev => ({ ...prev, status: 'answered' }));
                startTime.current = Date.now();
                startDurationTimer();
            });

            call.on('disconnect', () => {
                currentCallRef.current = null;
                setCallState({
                    status: 'idle',
                    duration: 0,
                    muted: false,
                    error: null
                });
                stopDurationTimer();
            });

            call.on('cancel', () => {
                currentCallRef.current = null;
                setCallState({
                    status: 'idle',
                    duration: 0,
                    muted: false,
                    error: null
                });
                stopDurationTimer();
            });

            call.on('reject', () => {
                currentCallRef.current = null;
                setCallState({
                    status: 'idle',
                    duration: 0,
                    muted: false,
                    error: null
                });
                stopDurationTimer();
            });


            call.on('warning', (name, data) => {
                // suppress warning-level events that might contain user gesture issues
                console.log('Call warning:', name, data);
            });

        } catch(error) {
            reportError(error);
        }
    }

    const getCallStatusLabel = () => {
        switch(callState.status) {
            case 'connecting':
            return 'Connecting...';

            case 'answered':
            return formatDuration(callState.duration);

            case 'disconnecting':
            return 'Disconnecting...';

            case 'error':
            return 'Call Failed';

            case 'rejected':
            return 'Call Rejected';

            default:
            return callState.status;
        }
    }

    const getContent = () => {

        const isLoading = callState.status === 'connecting' || callState.status === 'disconnecting';

        // no button is allowed if microphone permission hasnt been granted yet
        if(permissionGranted === false) {
            return null;
        }

        // determine if call is connected and render controls if applicable
        if(callState.status !== 'idle') {
            return (
                <div style={{ 
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    height: 45,
                    ...style
                }}>
                    <img 
                    alt={'End Call'}
                    className={'text-button'}
                    onClick={onEndCallClick}
                    title={'End Call'}
                    src={'images/twilio-end-call-red.png'}
                    style={{ 
                        height: 40,
                        marginRight: 8,
                        objectFit: 'contain',
                        width: 40
                    }} />
                    <div style={{ 
                        ...Appearance.styles.unstyledPanel(),
                        alignItems: 'center',
                        backgroundColor: getStateColor(),
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        flexGrow: 1,
                        height: 45
                    }}>
                        <span style={{
                            color: 'white',
                            display: 'block',
                            fontFamily: 'Zen Dots',
                            fontSize: 14,
                            fontWeight: 800,
                            textAlign: 'center'
                        }}>
                            {getCallStatusLabel().toUpperCase()}
                        </span>
                    </div>
                    <img 
                    alt={callState.muted ? 'Muted' : 'Unmuted'}
                    className={'text-button'}
                    onClick={onMuteToggleClick}
                    title={callState.muted ? 'Unmute' : 'Mute'}
                    src={callState.muted ? 'images/twilio-audio-disabled.png' : 'images/twilio-audio-enabled.png'}
                    style={{ 
                        height: 40,
                        marginLeft: 8,
                        objectFit: 'contain',
                        width: 40
                    }} />
                </div>
            );
        }

        return (
            <div style={style}>
                <Button
                color={'primary'}
                disabled={!deviceReady || isLoading || !lead || !lead.phone_number}
                label={title || 'Call'}
                loading={isLoading}
                onClick={onStartCallClick} />
            </div>
        );
    }

    const getStateColor = () => {
        switch(callState.status) {

            case 'answered':
            return Appearance.colors.primary();
            
            case 'disconnecting':
            case 'error':
            case 'rejected':
            return Appearance.colors.red;

            default:
            return Appearance.colors.grey();
        }
    }

    const formatDuration = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }

    const initDevice = async () => {
        try {

            // request microphone permission
            let stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            let tracks = stream.getTracks() || [];
            if(tracks.length === 0) {
                throw new Error('No microphone tracks found. Please verify that your microphone is connected and accessible.');
            }

            // loop through tracks and stop immediately to release microphone access
            tracks.forEach(track => track.stop());

            // request permission to play audio (required for some browsers to allow audio playback in future calls)
            const silentAudio = new Audio('data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA');
            silentAudio.play().catch(() => {});

            // create twilio device
            const device = new Device(tokenRef.current, {
                codecPreferences: ['opus', 'pcmu'],
                enableRingingState: true,
                logLevel: 1,
                edge: 'ashburn',
                sounds: {
                    disconnect: false,
                    incoming: false,
                    outgoing: false
                }
            });

            // store device reference in ref for access in event handlers
            deviceRef.current = device;

            // register device event listeners
            device.on('error', reportError);
            device.on('registered', () => {
                setDeviceReady(true);
                setPermissionGranted(true);
            });

            // register device
            await device.register();

        } catch(error) {
            utils.alert.show({
                title: 'Call Setup Failed',
                message: error?.message || 'An unknown error occurred'
            });
        }
    }

    const initDeviceRequest = async () => {
        try {

            // fetch twilio token from server
            const { token } = await Request.get(utils, '/twilio/', { type: 'generate_access_token'});
            tokenRef.current = token;

            // show alert to request microphone permission and initialize device on confirmation
            utils.alert.show({
                title: 'Microphone Access',
                message: 'To make and receive calls, we need permission to use your microphone. Calls may be recorded for training and quality purposes.',
                buttons: [{
                    key: 'confirm',
                    title: 'Continue',
                    style: 'default'
                },{
                    key: 'cancel',
                    title: 'Cancel',
                    style: 'cancel'
                }],
                onClick: async key => {
                    if(key === 'confirm') {
                        initDevice();
                        return;
                    }
                }
            });

        } catch(e) {
            console.error(e.message);
        }
    }

    const reportError = error => {

        // silently ignore user gesture errors
        if(error?.name === 'NotAllowedError' || error?.code === 'NotAllowedError') {
            console.warn('User gesture error (suppressed):', error.message);
            return;
        }

        // update call state with error and reset to idle status
        setCallState(prev => ({ 
            ...prev, 
            error: error.message,
            status: 'idle'
        }));

        // show alert with error message
        utils.alert.show({
            title: 'Oops!',
            message: error.message
        });
    }

    const startDurationTimer = () => {
        durationInterval.current = setInterval(() => {
            if(startTime.current) {
                const elapsed = Math.floor((Date.now() - startTime.current) / 1000);
                setCallState(prev => ({ ...prev, duration: elapsed }));
            }
        }, 1000);
    }

    const stopDurationTimer = () => {
        if(durationInterval.current) {
            clearInterval(durationInterval.current);
            durationInterval.current = null;
        }
        startTime.current = null;
    }

    useEffect(() => {

        // initialize device request to prompt for microphone permission
        initDeviceRequest();
        
        // add global unhandled rejection handler for user gesture errors
        // use capturing phase to ensure we catch it before React's error boundary
        window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
        
        // cleanup on unmount
        return () => {

            stopDurationTimer();
            
            // remove unhandled rejection handler
            window.removeEventListener('unhandledrejection', handleUnhandledRejection, true);

            // disconnect any active call
            currentCallRef.current?.disconnect();
            currentCallRef.current = null;
            
            // destroy device
            deviceRef.current?.destroy();
            deviceRef.current = null;
        };
        
    }, []);

    return getContent();
};

export default CallButton;
